import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators }                   from '@angular/forms';
import { Router, ActivatedRoute }                                             from '@angular/router';
import { WizardConfigService }                                                from '@cs/components/wizard';
import { Subject }                                                            from 'rxjs';
import { takeUntil, finalize }                                                from 'rxjs/operators';
import { RequestAccessConfigService }                                         from '../request-access-config.service';
import { RequestDetails, DepartmentOption, BusinessUnitOption }               from '../models';
import { CsHttpRequestOptions, Result }                                       from '@cs/core';
import { WizardStepsDataDescribed }                                           from '@cs/components/wizard/models/wizard-data-described';
import { LoginService }                                                       from '@cs/performance-manager/login';
import { TranslateService }                                                   from '@ngx-translate/core';

@Component({
												standalone : false,
												selector   : 'pmc-request-access-step-two',
												templateUrl: './step-two.component.html',
												styleUrls  : ['./step-two.component.scss']
											})
export class RequestAccessStepTwoComponent implements OnInit, OnDestroy {
	requestForm!: UntypedFormGroup;
	isValidatingToken                           = true;
	isSubmitting                                = false;
	tokenError: string | null                   = null;
	token                                       = '';
	departments: DepartmentOption[]             = [];
	businessUnits: BusinessUnitOption[]         = [];
	wizardData: WizardStepsDataDescribed | null = null;
	requireToken                                = true; // Track whether this route requires token validation

	constructor(
		private fb: UntypedFormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private config: RequestAccessConfigService,
		private wizardConfig: WizardConfigService,
		private loginService: LoginService,
		private translate: TranslateService,
		private changeRef: ChangeDetectorRef
	) {
		// Check if token is required for this route
		this.requireToken = this.route.snapshot.data['requireToken'] !== false;

		if (this.requireToken) {
			// Email link flow: hide the card header — wizard takes over the full card
			this.loginService.setHideHeader(true);
		} else {
			// Check your email flow: show card header with confirmation title/subtitle
			this.translate.get('REQUEST_ACCESS')
							.subscribe(value => this.loginService.setTitleMessage(value));
			this.translate.get('SUCCESS_MESSAGE')
							.subscribe(value => this.loginService.setUnderTitleMessage(value));
		}
	}

	ngOnInit(): void {

		this.validateToken();


	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();

		if (this.requireToken) {
			// Restore header that was hidden for the wizard flow
			this.loginService.setHideHeader(false);
			this.loginService.setHideLogo(false);
		}
		// Always restore the title and clear the subtitle on leave, regardless
		// of which path (token or check-email) was active.
		this.translate.get('LOGIN_WELCOME_MESSAGE')
						.subscribe(msg => this.loginService.setTitleMessage(msg));
		this.loginService.setUnderTitleMessage('');
	}

	onSubmit(): void {
		if (this.requestForm.valid && !this.isSubmitting) {
			this.isSubmitting = true;

			const formValues                     = this.requestForm.value;
			const requestDetails: RequestDetails = {
				fullName    : formValues.fullName,
				department  : formValues.department,
				businessUnit: formValues.businessUnit,
				managerEmail: formValues.managerEmail,
				ldapUsername: formValues.ldapUsername || undefined
			};

			const errorHandler: CsHttpRequestOptions = {
				errorResponseHandler: () => true // Let CS handle errors
			};

			this.config.completeRequest(requestDetails, this.token, errorHandler)
							.pipe(
								takeUntil(this.destroy$),
								finalize(() => this.isSubmitting = false)
							)
							.subscribe({
																			next : (result: Result<any>) => {
																				if (result.isSuccess) {
																					// Navigate to confirmation page with request ID
																					this.router.navigate(['/request-access/confirmation', result.value.requestId]);
																				} else {
																					console.error('Request submission failed:', result);
																				}
																			},
																			error: (error) => {
																				console.error('Request submission error:', error);
																			}
																		});
		}
	}

	goToLogin(): void {
		this.router.navigate(['/login']);
	}

	goToRequestAccess(): void {
		this.router.navigate(['/request-access']);
	}

	private destroy$ = new Subject<void>();

	private validateToken(): void {
		// Check if this route requires a token from route data
		const requireToken = this.route.snapshot.data['requireToken'];

		// If token is not required, skip validation
		if (requireToken === false) {
			this.isValidatingToken = false;
			this.tokenError        = null;
			return;
		}

		this.token = this.route.snapshot.params['token'];

		if (!this.token) {
			this.tokenError        = 'Invalid request link. Please start a new request.';
			this.isValidatingToken = false;
			return;
		}
		const errorHandler                = new CsHttpRequestOptions();
		errorHandler.errorResponseHandler = () => false; // Handle errors manually

		this.config.verifyToken(this.token, errorHandler)
						.pipe(
							takeUntil(this.destroy$),
							finalize(() => this.isValidatingToken = false)
						)
						.subscribe({
																		next : (result: Result<boolean>) => {
																			if (!result.isSuccess || !result.value) {
																				this.tokenError = 'Your request link has expired. Please start a new request.';
																			}
																			this.initWizard();
																		},
																		error: (error) => {
																			console.error('Token validation error:', error);
																			this.tokenError = 'Invalid request link. Please start a new request.';
																		}
																	});
	}

	private initWizard(): void {
		this.wizardConfig.getWizardSteps()
						.subscribe(value => {
							this.wizardData = value.value;
							this.changeRef.detectChanges();
						});
	}
}
